import {RANDOM_FACTION_CONFIG, RANDOM_FACTION_ID} from '../../configs/factions';
import {getFactionSkinArtPath} from '../../utils/factions/getFactionSkinArtPath';
import {resolveFactionSkinIconPath} from '../../utils/factions/resolveFactionSkinIconPath';

/**
 * Полный арт фракции в образе — тот, что показывают арт своей выбранной
 * фракции над списком игроков и карточка фракции (FactionCardOverlay): формула
 * общая (getFactionSkinArtPath), и предзагрузка грузит ровно то, что откроет
 * карточка. Сверх нее — «Случайная фракция», которую можно выбрать в лобби.
 *
 * @param {string} factionId
 * @param {string} [skinId]
 * @returns {string}
 */
export const getLobbyFactionArtPath = (factionId, skinId) => {
  // «Случайная фракция»: у мока нет образов — изображение из его конфига
  if (factionId === RANDOM_FACTION_ID) {
    return RANDOM_FACTION_CONFIG.imagePath;
  }
  return getFactionSkinArtPath(factionId, skinId);
};

/**
 * Арт своей фракции в своем образе. Образ — из selectedSkins сервера (их
 * присылают в рейтинговом лобби, когда набор раскрыт), иначе — из своей
 * раскладки auth.factionLoadouts (обычное лобби, до раскрытия набора).
 * player.selectedSkin не берем: это образ одной лишь выбранной фракции, а
 * адреса нужны для всех своих сразу — и до выбора (предзагрузка, старт выбора
 * в карусели); сервер берет его из той же раскладки
 *
 * @param {Object} ownPlayer
 * @param {string} factionId
 * @param {Object} [factionLoadouts] - auth.factionLoadouts
 * @returns {string}
 */
export const getOwnLobbyFactionArtPath = (ownPlayer, factionId, factionLoadouts) => getLobbyFactionArtPath(
  factionId,
  ownPlayer?.selectedSkins?.[factionId] || factionLoadouts?.[factionId]?.selectedSkin,
);

// Образ фракции игрока — как в карточке по его иконке (см. openFactionCard)
const getPlayerSkinId = (player, factionId) => player.selectedSkins?.[factionId]
  || (factionId === player.factionId ? player.selectedSkin : undefined);

const getDealtFactionIds = (player) => player?.availableFactionIds || [];

/**
 * Что грузит мобильное лобби и в каком порядке:
 * 1. иконки фракций в плашках игроков (их рисует и сам экран своими <img> —
 *    повторный запрос того же адреса браузер отдаст из кэша; очередь по ним
 *    узнает, когда браться за полные изображения);
 * 2. полные арты своих фракций (набор, «Случайная», выбранная) — своя
 *    выбранная фракция показывается над списком, только когда загрузка их
 *    всех кончилась (упавшая не держит остальных): переключение выбора
 *    мгновенное;
 * 3. полные арты фракций соперников — для их карточек, в фоне.
 *
 * @param {Object} params
 * @param {Object[]} params.players
 * @param {string} params.ownPlayerId
 * @param {Object} [params.factionLoadouts] - auth.factionLoadouts
 * @returns {{iconPaths: string[], ownArtPaths: string[], otherArtPaths: string[]}}
 */
export const getLobbyPreloadPaths = ({players, ownPlayerId, factionLoadouts}) => {
  const ownPlayer = players.find((player) => player.id === ownPlayerId);
  const otherPlayers = players.filter((player) => player.id !== ownPlayerId);

  const iconPaths = [ownPlayer, ...otherPlayers].filter(Boolean).flatMap((player) => (
    getDealtFactionIds(player).map((factionId) => resolveFactionSkinIconPath(factionId, player.selectedSkins?.[factionId]))
  ));
  // «Случайная» в рейтинговом лобби выбирается наравне с выданными (карусель
  // выбора). Выбранная фракция — и без набора (обычное лобби): там она одна и
  // есть «свои»
  const dealtFactionIds = getDealtFactionIds(ownPlayer);
  const ownFactionIds = [
    ...dealtFactionIds,
    ...(dealtFactionIds.length > 0 ? [RANDOM_FACTION_ID] : []),
    ownPlayer?.factionId,
  ].filter(Boolean);
  const ownArtPaths = ownFactionIds.map((factionId) => getOwnLobbyFactionArtPath(ownPlayer, factionId, factionLoadouts));
  const otherArtPaths = otherPlayers.flatMap((player) => (
    getDealtFactionIds(player).map((factionId) => getLobbyFactionArtPath(factionId, getPlayerSkinId(player, factionId)))
  ));

  const unique = (paths) => [...new Set(paths)];
  return {
    iconPaths: unique(iconPaths),
    ownArtPaths: unique(ownArtPaths),
    otherArtPaths: unique(otherArtPaths).filter((path) => !ownArtPaths.includes(path)),
  };
};
